import { Amplify, type ResourcesConfig } from 'aws-amplify';
import {
  fetchAuthSession,
  signInWithRedirect,
  signOut,
} from 'aws-amplify/auth';

import { dvvAuthConfig, woningNetSsoAuthConfig } from '../aws-exports';
import { isSsoEnabled } from '../config';
import {
  getCurrentAuthProvider,
  getPersistedAuthProvider,
  setCurrentAuthProvider,
} from './authProviderState';

export { getCurrentAuthProvider } from './authProviderState';

/**
 * Configure Amplify for WoningNet SSO authentication.
 * This switches the Amplify configuration to use the WoningNet SSO Cognito pool.
 */
export const configureWoningNetSso = (): void => {
  if (!isSsoEnabled()) {
    return;
  }

  const ssoConfig = woningNetSsoAuthConfig();
  Amplify.configure(ssoConfig);
  setCurrentAuthProvider('woningnet');
};

/**
 * Restore WoningNet SSO as the current auth provider when the app loads
 * (e.g. after a full page reload). Call this after configuring Amplify with
 * DVV so that protected routes still recognize an existing WoningNet session.
 */
export const restoreWoningNetAuthFromStorage = (): void => {
  if (!isSsoEnabled() || getPersistedAuthProvider() !== 'woningnet') {
    return;
  }
  configureWoningNetSso();
};

/**
 * Configure Amplify for DVV magic link authentication.
 * This switches the Amplify configuration back to the DVV Cognito pool.
 */
export const configureDvvAuth = (config: ResourcesConfig): void => {
  Amplify.configure(config);
  setCurrentAuthProvider('dvv');
};

/**
 * Check if the user is authenticated via WoningNet SSO.
 */
export const isWoningNetAuthenticated = async (): Promise<boolean> => {
  if (getCurrentAuthProvider() !== 'woningnet') {
    return false;
  }

  try {
    const { tokens } = await fetchAuthSession();
    return Boolean(tokens);
  } catch {
    return false;
  }
};

/**
 * Initiate WoningNet SSO login flow.
 * This will redirect the user to the WoningNet SSO page.
 */
export const initiateWoningNetSsoLogin = async (): Promise<void> => {
  if (!isSsoEnabled()) {
    throw new Error('WoningNet SSO is not enabled for this environment');
  }

  // Ensure we're configured for WoningNet SSO
  configureWoningNetSso();

  await signInWithRedirect({
    provider: {
      custom: 'woningnet',
    },
  });
};

/**
 * Handle the OAuth callback after WoningNet SSO redirect.
 * This should be called when the user is redirected back to the app with a code parameter.
 */
export const handleWoningNetSsoCallback = async (): Promise<boolean> => {
  const url = new URL(window.location.href);

  if (!url.searchParams.has('code')) {
    return false;
  }

  try {
    // Ensure we're configured for WoningNet SSO
    configureWoningNetSso();

    // Fetch the auth session to complete the token exchange
    await fetchAuthSession();

    // Give the token exchange a moment to complete
    await new Promise<void>((resolve) => {
      setTimeout(() => { resolve(); }, 500);
    });

    // Clean up the URL by removing the code parameter
    const cleanUrl = `${url.origin}${url.pathname}`;
    window.history.replaceState({}, document.title, cleanUrl);

    return true;
  } catch {
    return false;
  }
};

/**
 * Sign out from WoningNet SSO.
 * Reconfigures Amplify back to DVV and clears persisted provider so reload uses DVV.
 */
export const signOutWoningNetSso = async (): Promise<void> => {
  if (getCurrentAuthProvider() === 'woningnet') {
    await signOut();
    configureDvvAuth(dvvAuthConfig);
  }
};
